'use client';

/**
 * Bounds the app ⇄ Keycloak sign-in loop.
 *
 * {@link isAuthPage} stops the loop that runs through the `/auth/*` pages, but
 * not the one that runs through an ordinary page: when enrichment behind
 * `/api/auth/get-session` fails for a user that *is* provisioned — a deleted
 * user row, or a transient database failure — the session reads `null`, the app
 * signs the user out, sign-in restarts, Keycloak's still-valid SSO cookie
 * returns immediately, enrichment fails again, and the browser spins forever.
 *
 * Nothing at that point can tell a failing retry from a first sign-in, so the
 * loop is bounded by counting consecutive redirects: sign-in that keeps failing
 * to produce a usable session is stopped and surfaced to the user rather than
 * retried without end.
 */

/** Consecutive sign-in redirects tolerated inside {@link ATTEMPT_WINDOW_MS}. */
export const MAX_SIGN_IN_ATTEMPTS = 3;

/**
 * `?error=` code the user is sent to the sign-in error page with, so a stopped
 * loop is presented exactly like every other auth failure instead of through a
 * screen of its own.
 */
export const SIGN_IN_LOOP_ERROR_CODE = 'sign_in_loop_detected';

/**
 * Attempts further apart than this belong to a new sign-in episode, not a loop —
 * a real loop iterates in well under a second, while a user typing credentials
 * at Keycloak takes far longer.
 */
export const ATTEMPT_WINDOW_MS = 30_000;

/** Per-tab, so a stuck tab never blocks sign-in anywhere else. */
export const SIGN_IN_ATTEMPTS_STORAGE_KEY = 'workPermit.signInAttempts';

type SignInAttempts = {
  readonly count: number;
  readonly lastAttemptAt: number;
};

/** `sign-in` → redirect to Keycloak; `loop-detected` → stop and show the user why. */
export type SignInAttemptDecision = 'sign-in' | 'loop-detected';

/**
 * Folds a new attempt into the running count, restarting the count when the
 * previous attempt is older than {@link ATTEMPT_WINDOW_MS}.
 */
const nextSignInAttempts = (
  previous: SignInAttempts | null,
  now: number,
): SignInAttempts => {
  if (!previous || now - previous.lastAttemptAt > ATTEMPT_WINDOW_MS) {
    return { count: 1, lastAttemptAt: now };
  }

  return { count: previous.count + 1, lastAttemptAt: now };
};

/** Serialized as `<count>:<lastAttemptAt>` — two numbers need no JSON. */
const serialize = (attempts: SignInAttempts): string =>
  `${attempts.count}:${attempts.lastAttemptAt}`;

const deserialize = (raw: string): SignInAttempts | null => {
  const [count, lastAttemptAt] = raw.split(':').map(Number);

  if (!Number.isFinite(count) || !Number.isFinite(lastAttemptAt)) {
    return null;
  }

  return { count, lastAttemptAt };
};

/** Reads the stored attempts, treating anything unreadable as "no attempts yet". */
const readSignInAttempts = (): SignInAttempts | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(SIGN_IN_ATTEMPTS_STORAGE_KEY);

    return raw ? deserialize(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Records a sign-in redirect and reports whether it should go ahead.
 *
 * @param now - Current epoch milliseconds; injectable for tests.
 * @returns `loop-detected` once {@link MAX_SIGN_IN_ATTEMPTS} consecutive
 *   redirects inside the window have failed to produce a session.
 */
export const registerSignInAttempt = (
  now: number = Date.now(),
): SignInAttemptDecision => {
  const attempts = nextSignInAttempts(readSignInAttempts(), now);

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(
        SIGN_IN_ATTEMPTS_STORAGE_KEY,
        serialize(attempts),
      );
    } catch {
      // A storage write that fails (private mode, quota) must never block
      // sign-in — the guard simply stops counting.
    }
  }

  return attempts.count > MAX_SIGN_IN_ATTEMPTS ? 'loop-detected' : 'sign-in';
};

/** Clears the count. Called as soon as a session resolves as authenticated. */
export const clearSignInAttempts = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(SIGN_IN_ATTEMPTS_STORAGE_KEY);
  } catch {
    // Nothing to do: an unreadable store already reads as "no attempts yet".
  }
};
